import * as Dialog from "@radix-ui/react-dialog";
import { useState, useRef, useEffect } from "react";
import { jsPDF } from "jspdf";
import { TERMS_CONTENT, TERMS_SECTIONS, TermsSection, TermsSubsection } from "../../pages/signup/termsContent";

interface PortalTermsModalProps {
  open: boolean;
  onAccept: () => void;
  onDecline: () => void;
  isSubmitting?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

export default function PortalTermsModal({ open, onAccept, onDecline, isSubmitting = false, error, onRetry }: PortalTermsModalProps) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setHasScrolledToBottom(false);
    }
  }, [open]);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setHasScrolledToBottom(true);
      }
    }
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    const margin = 20;
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const textWidth = pageWidth - margin * 2;
    let yPos = 20;

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Terms & Conditions and Confidentiality Agreement", pageWidth / 2, yPos, {
      align: "center",
    });
    yPos += 10;

    doc.setFontSize(12);
    doc.text("FixFinder Inc. — Research Portal", pageWidth / 2, yPos, { align: "center" });
    yPos += 5;
    doc.text("Version 2.1 — Effective March 16, 2026", pageWidth / 2, yPos, { align: "center" });
    yPos += 15;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    const lines = TERMS_CONTENT.split("\n");
    lines.forEach((line: string) => {
      if (yPos > pageHeight - 30) {
        doc.addPage();
        yPos = 20;
      }

      if (line.match(/^\d+\./)) {
        doc.setFont("helvetica", "bold");
        yPos += 5;
      } else {
        doc.setFont("helvetica", "normal");
      }

      const splitText = doc.splitTextToSize(line, textWidth);
      splitText.forEach((textLine: string) => {
        if (yPos > pageHeight - 30) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(textLine, margin, yPos);
        yPos += 5;
      });
    });

    doc.save("FixFinder-Terms-and-Conditions-v2.1.pdf");
  };

  return (
    <Dialog.Root open={open} onOpenChange={() => {}}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100]" />
        <Dialog.Content 
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-3xl max-h-[85vh] bg-[#0A1628] rounded-2xl shadow-2xl border border-white/10 z-[100] flex flex-col"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div>
              <Dialog.Title className="text-xl font-bold text-white">
                Terms & Conditions (v2.1)
              </Dialog.Title>
              <p className="text-sm text-gray-400 mt-1">
                Please review and accept to continue using the Research Portal
              </p>
            </div>
            <button
              onClick={downloadPDF}
              className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors text-sm"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download PDF
            </button>
          </div>

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto p-6 text-gray-300 text-sm space-y-4"
          >
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
              <p className="text-yellow-300 text-sm">
                <strong>Important:</strong> You must scroll to the bottom and review all terms before you can accept.
                Declining will log you out of the portal.
              </p>
            </div>

            <p className="text-gray-400 mb-4">
              FixFinder Inc. — Research Portal
            </p>
            <p className="text-gray-400 italic mb-6">
              By clicking "I Agree", you acknowledge that you have read, understood, and
              agree to be bound by this Agreement.
            </p>

            {TERMS_SECTIONS.map((section: TermsSection, index: number) => (
              <div key={index} className="mb-6">
                <h3 className="text-white font-semibold mb-3">{section.title}</h3>
                {section.subsections.map((subsection: TermsSubsection, subIndex: number) => (
                  <div key={subIndex} className="mb-4">
                    {subsection.heading && (
                      <h4 className="text-gray-200 font-medium mb-2">{subsection.heading}</h4>
                    )}
                    <ul className="list-disc pl-5 space-y-2">
                      {subsection.items.map((item: string, itemIndex: number) => (
                        <li key={itemIndex}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="p-6 border-t border-white/10">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 flex items-center justify-between">
                <p className="text-red-400 text-sm">{error}</p>
                {onRetry && (
                  <button
                    onClick={onRetry}
                    className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-sm rounded transition-colors"
                  >
                    Retry
                  </button>
                )}
              </div>
            )}
            <div className="flex items-center justify-between gap-4">
              <p className="text-gray-400 text-sm">
                {hasScrolledToBottom ? (
                  <span className="text-green-400">You've reviewed the terms</span>
                ) : (
                  "Please scroll to review all terms"
                )}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={onDecline}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-lg transition-colors disabled:opacity-50"
                >
                  Decline & Logout
                </button>
                <button
                  onClick={onAccept}
                  disabled={!hasScrolledToBottom || isSubmitting}
                  className="px-5 py-2 bg-[#0193F8] hover:bg-[#0173C8] disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Processing...
                    </>
                  ) : (
                    "I Agree"
                  )}
                </button>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
